// client/src/views/Sidebar_parts/RegionMapsDrawer.js
import React, { useState } from "react";

function RegionMapsDrawer({
  isMapOwner,
  extraRegionMaps = [],
  parentMapId,
  mapDisplayName,
  onRegionMapClick,
  sectionNameRegionMaps,
  user,
  onRegionMapsChanged,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newMapName, setNewMapName] = useState("");
  const [newMapFile, setNewMapFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingMapId, setEditingMapId] = useState(null);
  const [editingMapName, setEditingMapName] = useState("");

  const toggleDrawer = () => {
    setDrawerOpen((open) => !open);
    if (drawerOpen) setShowUploadForm(false);
  };

  const handleAddNewMapClick = () => {
    setShowUploadForm(true);
    setNewMapName("");
    setNewMapFile(null);
    setUploadError(null);
  };

  const handleCancelUpload = () => {
    setShowUploadForm(false);
    setUploadError(null);
  };

  const handleFileChange = (e) => {
    setNewMapFile(e.target.files[0]);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!newMapName.trim() || !newMapFile) {
      setUploadError("Please provide a map name and select a file.");
      return;
    }
    if (!parentMapId) {
      setUploadError("Parent map ID is missing.");
      return;
    }
    if (newMapFile.size > 10 * 1024 * 1024) {
      setUploadError("File size exceeds 10MB limit.");
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("display_name", newMapName.trim());
      formData.append("uploader_id", user?.id);
      formData.append("map", newMapFile);

      const response = await fetch(`/api/child-maps/${parentMapId}/child`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        setUploadError(data.error || "Upload failed");
      } else {
        setShowUploadForm(false);
        setNewMapName("");
        setNewMapFile(null);
        if (typeof onRegionMapsChanged === "function") {
          onRegionMapsChanged();
        }
      }
    } catch (err) {
      setUploadError("An error occurred during upload.");
    } finally {
      setUploading(false);
    }
  };

  const startEditingMap = (region) => {
    setEditingMapId(region.id);
    setEditingMapName(region.name);
  };

  const cancelEditing = () => {
    setEditingMapId(null);
    setEditingMapName("");
  };

  const saveEditingMap = async () => {
    if (!editingMapName.trim()) return;
    try {
      const res = await fetch(`/api/child-maps/${editingMapId}/child`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          display_name: editingMapName.trim(),
          uploader_id: user?.id,
        }),
      });
      if (res.ok) {
        if (typeof onRegionMapsChanged === "function") {
          onRegionMapsChanged();
        }
        cancelEditing();
      } else {
        alert("Failed to update map name.");
      }
    } catch {
      alert("Error updating map name.");
    }
  };

  const deleteMap = async () => {
    if (!window.confirm("Are you sure you want to delete this map?")) return;
    try {
      const res = await fetch(`/api/child-maps/${editingMapId}/child`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ uploader_id: user?.id }),
      });
      if (res.ok) {
        if (typeof onRegionMapsChanged === "function") {
          onRegionMapsChanged();
        }
        cancelEditing();
      } else {
        alert("Failed to delete map.");
      }
    } catch {
      alert("Error deleting map name.");
    }
  };

  if (!isMapOwner && extraRegionMaps.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={toggleDrawer}
        style={{
          backgroundColor: "#000",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "10px 16px",
          fontWeight: 700,
          fontSize: 16,
          cursor: "pointer",
          width: "90%",
          margin: "0 auto 10px auto",
          boxShadow: "0 2px 8px #0008",
          transition: "background-color 0.2s",
          userSelect: "none",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#222")}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#000")}
        aria-expanded={drawerOpen}
        aria-controls="region-maps-drawer"
      >
        {sectionNameRegionMaps}
      </button>

      <div
        id="region-maps-drawer"
        style={{
          maxHeight: drawerOpen ? 260 : 0,
          overflowY: drawerOpen ? "auto" : "hidden",
          overflowX: "hidden",
          transition: "max-height 0.3s ease",
          backgroundColor: "#282931",
          borderRadius: 8,
          margin: "0 12px 12px 12px",
          color: "#eee",
          fontSize: 14,
          padding: drawerOpen ? "10px 14px" : "0 14px",
          boxShadow: drawerOpen ? "0 4px 12px #0009" : "none",
        }}
      >
        {extraRegionMaps.length === 0 && !showUploadForm && (
          <div
            style={{
              fontStyle: "italic",
              marginBottom: 10,
              color: "#999",
              userSelect: "none",
            }}
          >
            No region maps yet
          </div>
        )}

        {!showUploadForm && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            <li
              key={"parent-map"}
              style={{
                padding: "6px 0",
                cursor: parentMapId ? "pointer" : "default",
                fontWeight: "bold",
                color: "#ffd870",
                background:
                  !extraRegionMaps.some((r) => r.id === parentMapId) &&
                  parentMapId
                    ? "#403300"
                    : "transparent",
                borderRadius: 5,
                opacity: 1,
              }}
              onClick={() => {
                if (onRegionMapClick && parentMapId) {
                  onRegionMapClick({
                    id: parentMapId,
                    name: mapDisplayName,
                    isParent: true,
                  });
                }
              }}
            >
              {mapDisplayName}{" "}
              {extraRegionMaps.some((r) => r.id === parentMapId)
                ? "(Parent)"
                : ""}
            </li>

            {extraRegionMaps.map((region) => {
              const isEditing = editingMapId === region.id;
              return (
                <li
                  key={region.id}
                  style={{
                    padding: "6px 0",
                    cursor: region.id === parentMapId ? "default" : "pointer",
                    background:
                      region.id === parentMapId ? "#ffd87033" : "transparent",
                    color: "#fff",
                    fontWeight: region.id === parentMapId ? 700 : 400,
                    borderRadius: 5,
                    opacity: region.id === parentMapId ? 0.85 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    minWidth: 0,
                    flexDirection: isEditing ? "column" : "row",
                    alignItems: isEditing ? "stretch" : "center",
                  }}
                  onClick={() => {
                    if (
                      !isEditing &&
                      onRegionMapClick &&
                      region.id !== parentMapId
                    ) {
                      onRegionMapClick(region);
                    }
                  }}
                >
                  {!isEditing ? (
                    <>
                      <span
                        style={{
                          flexGrow: 1,
                          minWidth: 0,
                          cursor:
                            region.id === parentMapId ? "default" : "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={region.name}
                      >
                        {region.name}
                      </span>
                      {isMapOwner && region.id !== parentMapId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingMap(region);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#ffc73c",
                            fontSize: 16,
                            padding: 0,
                            userSelect: "none",
                            flex: "0 0 auto",
                            marginLeft: 8,
                          }}
                          aria-label={`Edit map ${region.name}`}
                          title={`Edit map ${region.name}`}
                        >
                          🖉
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={editingMapName}
                        onChange={(e) => setEditingMapName(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          borderRadius: 4,
                          border: "1px solid #555",
                          backgroundColor: "#222",
                          color: "#eee",
                          marginRight: 0,
                          boxSizing: "border-box",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          gap: 8,
                          flex: "0 0 auto",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEditing();
                          }}
                          style={{
                            flex: 1,
                            backgroundColor: "#888",
                            border: "none",
                            borderRadius: 4,
                            padding: "6px 8px",
                            color: "#fff",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                          aria-label="Cancel"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveEditingMap();
                          }}
                          style={{
                            flex: 1,
                            backgroundColor: "#4cc153",
                            border: "none",
                            borderRadius: 4,
                            padding: "6px 8px",
                            color: "#111",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                          aria-label="Save"
                        >
                          Save
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMap();
                          }}
                          style={{
                            flex: 1,
                            backgroundColor: "#ff5959",
                            border: "none",
                            borderRadius: 4,
                            padding: "6px 8px",
                            color: "#fff",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                          aria-label="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}

            {isMapOwner && (
              <li
                style={{
                  padding: "6px 0",
                  cursor: "pointer",
                  fontWeight: "bold",
                  borderTop: "1px solid #444",
                  marginTop: 8,
                  color: "#ffd870",
                }}
                onClick={handleAddNewMapClick}
              >
                + Add New Map
              </li>
            )}
          </ul>
        )}

        {showUploadForm && (
          <form
            onSubmit={handleUploadSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginTop: 8,
            }}
          >
            <input
              type="text"
              placeholder="Map Name"
              value={newMapName}
              onChange={(e) => setNewMapName(e.target.value)}
              disabled={uploading}
              required
              style={{
                padding: "6px 8px",
                borderRadius: 6,
                border: "1.5px solid #444",
                fontSize: 14,
                backgroundColor: "#222",
                color: "#eee",
              }}
            />
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading}
              required
              style={{ color: "#eee" }}
            />
            {uploadError && (
              <div
                style={{
                  color: "#ff5959",
                  fontWeight: "bold",
                  fontSize: 13,
                }}
              >
                {uploadError}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="submit"
                disabled={uploading}
                style={{
                  backgroundColor: "#ffc73c",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontWeight: 700,
                  cursor: uploading ? "wait" : "pointer",
                  flexGrow: 1,
                  color: "#222",
                  fontSize: 16,
                }}
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
              <button
                type="button"
                onClick={handleCancelUpload}
                disabled={uploading}
                style={{
                  backgroundColor: "#444",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontWeight: 700,
                  cursor: uploading ? "wait" : "pointer",
                  flexGrow: 1,
                  color: "#eee",
                  fontSize: 16,
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

export default RegionMapsDrawer;
